import React, { useCallback, useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useLocation, useParams } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Container } from '@mui/material';

import OfferBreadcrumbs from '~/components/Common/Elements/OfferBreadcrumbs';
import PageHeader from '~/components/Common/Elements/PageHeader';
import ErrorDisplay from '~/components/Common/ErrorDisplay';
import PropertyForm from '~/components/Common/Forms/PropertyForm';
import Spinner from '~/components/Common/Spinner';

import BedbankService from '~/services/BedbankService';
import ReservationService from '~/services/ReservationService';
import VendorsService from '~/services/VendorsService';

import preparePropertyFormSchema from './helpers/preparePropertyFormSchema';

export default function PropertyEditContainer() {
  const { id_vendor: vendorId, id_property: propertyId } = useParams<'id_vendor' | 'id_property'>();
  const location = useLocation();
  const returnTo = new URLSearchParams(location.search).get('returnTo');
  const offerBuilderReturnTo = returnTo?.startsWith('/offers/') ? returnTo : undefined;
  const returnToOfferBuilder = offerBuilderReturnTo ? () => window.location.assign(offerBuilderReturnTo) : undefined;

  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState();
  const [schema, setSchema] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [property, setProperty] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);

    const [propertySchemaResult, propertyResult, vendorResult] = await Promise.all([
      ReservationService.getPropertySchema(propertyId),
      ReservationService.getProperty(propertyId),
      VendorsService.getVendorById(vendorId),
    ]);

    if (propertyResult.result.parent_property_id) {
      const parent = await ReservationService.getProperty(propertyResult.result.parent_property_id);
      if (parent && parent.result) {
        propertyResult.result.parentProperty = parent.result;
      }
    }

    if (propertyResult.result.bedbank_property_id) {
      const bedbankProperty = await BedbankService.getPropertyById(propertyResult.result.bedbank_property_id);
      if (bedbankProperty && bedbankProperty.result) {
        propertyResult.result.bedbankProperty = bedbankProperty.result;
      }
    }

    setVendor(vendorResult.result);
    setProperty(propertyResult.result);
    setSchema(preparePropertyFormSchema(propertySchemaResult.put.body.schema, propertyResult.result));
    setLoading(false);
  }, [vendorId, propertyId]);

  useEffect(() => {
    fetchData().catch((err) => setError(err.mesage));
  }, [fetchData]);

  if (isLoading) {
    return (
      <Container>
        <Spinner />
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorDisplay message={error} />
      </Container>
    );
  }

  const Form = PropertyForm as any;

  return (
    <Container>
      <Helmet>
        <title>
          Vendors | {vendor?.name || vendorId} | {property?.name} | Edit Property
        </title>
      </Helmet>

      <OfferBreadcrumbs
        vendor={{ label: vendor?.vendor_name || vendor?.name || 'Vendor', href: `/vendors/${vendorId}` }}
        property={{ label: property?.name || 'Property', href: `/vendors/${vendorId}/properties/${propertyId}` }}
        currentPage="Edit property"
      />
      <PageHeader title="Edit property" />
      {offerBuilderReturnTo && (
        <Box sx={{ mb: 2 }}>
          <Button onClick={returnToOfferBuilder} variant="outlined" startIcon={<ArrowBackIcon />}>
            Back to Offer Builder
          </Button>
        </Box>
      )}

      <Box>
        <Form property={property} schema={schema} vendorId={vendorId} onSubmitSuccess={returnToOfferBuilder} />
      </Box>
    </Container>
  );
}
